define("Orders", [`Class`, `module`], function (Super, Self) {
	// Statuses that allow the user access to the order's content
	const COMPLETE_ORDER_STATUSES = [`COMPLETE`, `REFUND_REWARDED`];
	const COMPLETE_TRANSFER_STATUSES = [`ACCEPTED`];
	const ORDER_STATUS_LABELS = {
		ACTIVE: `Active`,
		OFFERED: `Accepted`, // fan-transfers endpoint has OFFERED while orders doesn't
		ACCEPTED: `Accepted`,
		CANCELED: `Canceled`,
		VOID: `Canceled`,
		FAILED: `Canceled`,
		QUEUE_CANCELED: `Canceled`,
		QUEUE_EXPIRED: `Expired`,
		QUEUE_DECLINED: `Declined`,
		COMPLETE: `Complete`,
		EXPIRED: `Expired`,
		PAST_DUE: `Past due`,
		PENDING: `Active`,
		INITIALIZED: `Pending`,
		PENDING_SETTLEMENT: `Active`,
		PENDING_DISBURSEMENT: `Active`,
		PENDING_REFUND_DISBURSEMENT: `Active`,
		PENDING_REFUND_SETTLEMENT: `Active`,
		CHARGEBACK_OPENED: `Active`,
		CHARGEBACK_WON: `Disputed`,
		CHARGEBACK_LOST: `Disputed`,
		PREARB_OPENED: `Disputed`,
		PREARB_WON: `Disputed`,
		PREARB_LOST: `Disputed`,
		CONTESTED: `Active`,
		QUEUED: `Waitlist`,
		REFUNDED: `Refunded`,
		REFUND_REWARDED: `Refunded`,
		INVOICE_APPROVED: `Invoice Approved`,
		INVOICE_SENT: `Invoice Sent`,
		INVOICE_CANCELED: `Invoice Canceled`,
		INVOICE_PENDING_PAYMENT: `Pending Payment`,
		PAID: `Paid`,
		PAID_OFF_EARLY: `Paid`,
	};
	const DELIVERY_TYPE_LABELS = {
		ELECTRONIC: `E-Ticket`,
		LIVE_STREAM: `Tixr Play`,
		NONE: `No delivery`,
		SHIPPING: `Shipping`,
		THIRD_PARTY: `Event organizer`,
		VIDEO_CHAT: `Tixr Play`,
		VOD: `Tixr Play`,
		WILLCALL: `Will call`,
	};

	return Super.inherit({
		namespace: Self.id,
		singleton: true,
		requires: [`App`, `System`, `Time`, `XHR`],
		api: {
			// Includes orders and transfers in a single array, paginated
			getFanOwnedItems: {
				async: true,
				public: true,
				async value(
					context,
					{ page, size, startDateBefore, startDateAfter, endDateBefore, endDateAfter, orderBy, orderByDirection }
				) {
					const { App, XHR } = this;
					const params = {
						page,
						size,
						startDateBefore,
						startDateAfter,
						endDateBefore,
						endDateAfter,
						orderBy,
						orderByDirection,
					};

					// Remove undefined params
					this.each(Object.keys(params), (key) => {
						if (typeof params[key] === `undefined`) {
							delete params[key];
						}
					});

					await this.require({ deps: [`User`] });
					const { id: userId } = await this.User.get({});

					const items = await XHR.ajax({
						type: `GET`,
						url: `/api/users/${userId}/fan-owned-items?${App.serializeParams(params)}`,
						ignoreErrors: true,
					});

					// Workaround to make property name and values consistent with other endpoints
					return this.map(items, (item) => ({
						...item,
						orderOrigin: item.type === `ORDER` ? `PURCHASE` : item.type,
					}));
				},
			},

			getFanOwnedItemById: {
				async: true,
				public: true,
				defaults: {
					origin: `PURCHASE`,
					force: false,
				},
				async value(context, { id, force, origin }) {
					if (!this.isString(id, 1)) {
						// ex. B1100
						throw new Error(`Orders.getFanOwnedItemById(): String required for options.id`);
					}

					await this.require({ deps: [`User`] });
					const { id: userId } = await this.User.get({ force });

					if (origin === `INVOICE`) {
						const invoices = await this.XHR.ajax({
							type: `GET`,
							url: `/api/users/${userId}/invoices`,
						});

						return this.find(invoices, { id });
					}

					const originPath = origin === `TRANSFER` ? `fan-transfers` : `orders`;

					return this.XHR.ajax({
						type: `GET`,
						url: `/api/users/${userId}/${originPath}/${id}`,
					});
				},
			},

			updateNames: {
				async: true,
				public: true,
				defaults: {
					tickets: {}, // a hash containing any ticket name updates to perform
				},
				value(context, options) {
					return this.require({
						deps: [`User`],
						then: () => {
							const { User, XHR } = this;
							const { orderId, orderOrigin, tickets } = options;
							const body = this.map(tickets, (ticket, serialId) => {
								const { firstName, lastName, saleId } = ticket;
								return { firstName, lastName, serialId, saleId };
							});
							let url;

							if (orderOrigin === `PURCHASE`) {
								url = `/api/orders/${orderId}/patrons?action=NAME_CHANGE`;
							} else if (orderOrigin === `TRANSFER`) {
								url = `/api/fan-transfers/${orderId}/patrons?action=NAME_CHANGE`;
							} else throw new Error(`Unrecognized orderOrigin: ${orderOrigin}`);

							return XHR.ajax({
								url,
								type: `PATCH`,
								REST: true,
								body,
								then: (response) =>
									User.get({
										force: true, // force a full user refresh, to ensure the order is up-to-date
										then: () => response,
									}),
							});
						},
					});
				},
			},

			transferItems: {
				async: true,
				public: true,
				value(context, options) {
					const { XHR } = this;
					const { email: recipientEmail, itemIds, orderId, orderOrigin } = options;
					const url = `/api/fan-transfer-offers`;
					const type = `POST`;
					const body = { itemIds, recipientEmail };
					if (orderOrigin === `PURCHASE`) {
						body.orderId = orderId;
					} else {
						body.transferId = orderId;
					}
					const REST = true;

					return XHR.ajax({ REST, body, type, url });
				},
			},

			payOffPaymentPlan: {
				async: true,
				public: true,
				defaults: {},
				value(context, options) {
					const { XHR } = this;
					const { orderId } = options;

					if (!this.isString(orderId)) {
						throw new Error(`Orders.payOffPaymentPlan(): options.orderId required.`);
					}

					const url = `/api/orders/${orderId}?action=PAYOFF_PAYMENT_PLAN`;
					const type = `PUT`;
					const deps = [`User`];

					return this.when({
						promises: [XHR.ajax({ url, type }), this.require({ deps })],
						spread: (response) => {
							const force = true;
							const done = true;
							this.User.get({ force, done });

							return response;
						},
					});
				},
			},

			cancelPaymentPlan: {
				async: true,
				public: true,
				defaults: {},
				value(context, options) {
					const { XHR } = this;
					const { orderId } = options;

					if (!this.isString(orderId)) {
						throw new Error(`Orders.cancelPaymentPlan(): options.orderId required.`);
					}

					const url = `/api/orders/${orderId}?action=CANCEL&id=${orderId}`;
					const type = `PUT`;
					const deps = [`User`];

					return this.when({
						promises: [XHR.ajax({ url, type }), this.require({ deps })],
						spread: (response) => {
							const force = true;
							const done = true;
							this.User.get({ force, done });

							return response;
						},
					});
				},
			},

			updatePaymentMethod: {
				// only makes sense for payment plans and waitlist
				async: true,
				public: true,
				defaults: {},
				value(context, options) {
					const { orderId, token } = options;

					if (!this.isString(orderId, 1) || !this.isString(token, 1)) {
						throw new Error(`Orders.cancelPaymentPlan(): options.orderId and options.token are required.`);
					}

					return this.when({
						promises: [
							this.XHR.ajax({
								type: `PUT`,
								url: `/api/orders/${orderId}?action=UPDATE_PAYMENT_TOKEN&token=${token}`,
							}),
							this.require({ deps: [`User`] }),
						],
						spread: (response) =>
							this.defer({
								// defer a moment, hoping to get an updated user object
								duration: 1200,
								then: () =>
									this.User.get({
										force: true,
										then: () => response,
									}),
							}),
					});
				},
			},

			updateQueued: {
				async: true,
				public: true,
				value(context, options) {
					const { orderId, paymentToken, expiration_unit, expiration_window } = options;
					let order;

					if (!this.isString(orderId, 1)) {
						throw new Error(`Order.updateQueued(): options.orderId required`);
					}

					return this.getFanOwnedItemById({
						id: orderId,
						then: (result) => {
							order = result;

							if (!this.isObject(order.queue_details)) {
								throw new Error(`Error loading queue settings for order "${orderId}"`);
							}
						},
					})
						.then(() => {
							// order fetch successfully, test for expiration change
							const { queue_details } = order;

							if (expiration_window !== queue_details.expiration_window) {
								// expiration window has changed
								const { queue_settings_id } = queue_details;

								return this.XHR.ajax({
									url: `/api/orders/${order.id}/queue-settings/${queue_settings_id}`,
									type: `PUT`,
									REST: true,
									data: { expiration_unit, expiration_window },
								});
							}
						})
						.then(() => {
							// check for payment token change
							const { total } = order;
							if (total > 0 && order.paymentToken !== paymentToken) {
								return this.updatePaymentMethod({
									token: paymentToken,
									orderId,
								});
							}
						});
				},
			},

			cancelQueued: {
				async: true,
				public: true,
				value(context, options) {
					const { orderId } = options;

					if (this.isUndefined(orderId)) {
						throw new Error(`Order.cancelQueued(): options.orderId required`);
					}

					const url = `/api/orders/${orderId}?action=CANCEL`;
					const type = `PUT`;
					const REST = true;
					const data = {};

					return this.XHR.ajax({ type, REST, data, url });
				},
			},

			updateShippingAddress: {
				async: true,
				public: true,
				value(context, options) {
					const { XHR } = this;
					const { id, origin, shippingAddress, deliveryType } = options;
					const body = { shippingAddress, deliveryType };
					const type = `PUT`;
					const path = origin === `PURCHASE` ? `orders` : `fan-transfers`;
					const url = `/api/${path}/${id}?action=UPDATE_SHIPPING_ADDRESS`;
					const REST = true;

					return XHR.ajax({ body, REST, type, url });
				},
			},

			normalize: {
				public: true,
				value(context, order) {
					// Whether to defer to the paymentSchedule's status
					const { Config, Time } = this;
					const now = Time.getTime();
					const { SALE_CATEGORY_ICONS, VIDEO_DELIVERY_TYPES } = Config;
					const { hiddenDetails, origin, paymentSchedule: schedule, status: orderStatus } = order;
					const paymentPlanEligible = orderStatus === `COMPLETE` || orderStatus === `PENDING`;
					const hasPaymentStatus = this.isObject(schedule) && this.isString(schedule.status, 1);
					const isPurchase = origin === `PURCHASE`;
					const completeStatuses = isPurchase ? COMPLETE_ORDER_STATUSES : COMPLETE_TRANSFER_STATUSES;
					const orderComplete = this.includes(completeStatuses, orderStatus);
					let hiddenDetailsList = []; // default empty list for aspects to avoid displaying
					let orderPlayable = false; // whether any sale contains playable media
					let seasonType = `NONE`; // default for non-season ticketing events
					let quantity = 0;
					let multiday;

					if (this.isString(hiddenDetails, 1)) {
						hiddenDetailsList = hiddenDetails.split(`,`);
					}

					// Take the payment plan status if one is present, otherwise the order status
					if (paymentPlanEligible && hasPaymentStatus) {
						const label = ORDER_STATUS_LABELS[order.paymentSchedule.status];
						order.statusText = `Payment Plan ${label}`;
					} else {
						order.statusText = ORDER_STATUS_LABELS[orderStatus];
					}

					if (!this.isString(order.statusText)) {
						order.statusText = orderStatus;
					}

					order.statusClass = this.includes([`COMPLETE`, `ACCEPTED`], orderStatus) ? `success` : null;

					if (order.tenderType === `REISSUE`) {
						order.total = 0;
					}

					if (this.isObject(order.seasonDetails)) {
						const { seasonEventType } = order.seasonDetails;
						seasonType = seasonEventType;
					}

					multiday = order.eventEndDate - order.eventDate > 86400000;

					if (!this.isString(order.paymentProcessorType)) {
						this.log({
							type: `warning`,
							message: `Order ${order.id} has no paymentProcessorType, defaulting to BRAINTREE`,
						});

						order.paymentProcessorType = `BRAINTREE`;
					}

					// Iterate over the individual items
					this.each(order.saleOrders, (saleOrder) => {
						const { deliveryType, items, saleCategory } = saleOrder;
						const friendlyLabel = DELIVERY_TYPE_LABELS[deliveryType];
						const deliveryTypeLabel = this.isString(friendlyLabel) ? friendlyLabel : deliveryType;
						const icon = SALE_CATEGORY_ICONS[saleCategory];

						let playable = false;
						let expired = true;
						let owned = false;
						let video;

						quantity += saleOrder.quantity;

						this.each(items, (item) => {
							this.each(
								{
									// a list of properties that go by both a short / long version
									anst: `activationSerialType`,
									ast: `activationState`,
									fn: `firstName`,
									ln: `lastName`,
									sn: `serialNumber`,
									st: `status`,
									t: `type`,
								},
								(longKey, shortKey) => {
									if (this.isUndefined(item[longKey])) {
										item[longKey] = item[shortKey];
									}
								}
							);

							const { st } = item;

							if (orderComplete && st === `VALID`) {
								owned = true;
							}
						});

						video = this.includes(VIDEO_DELIVERY_TYPES, deliveryType);
						if (video && owned) {
							const { availabilityDate, expirationDate, availabilityOffset, validTimeframe } = saleOrder;
							expired = this.isInt(expirationDate) && expirationDate < now;

							// NOTE: validTimeframe check should only done on initial load of the order (revisit when implementing periodic playability checks)
							if (validTimeframe) {
								playable = true;
							} else if (expired) {
								playable = false;
							} else if (!this.isInt(availabilityDate)) {
								// no availability constraint
								playable = true;
							} else if (!this.isInt(availabilityOffset)) {
								// no offset
								playable = true;
							} else {
								const offset = availabilityOffset * 60000; // 60sec * 1000ms
								const playabilityDate = availabilityDate - offset;
								playable = playabilityDate < now;
							}
						}

						if (playable) {
							orderPlayable = true; // toggle order summary value to true
						}

						// apply the calculated values
						this.assign(saleOrder, { deliveryTypeLabel, expired, icon, owned, playable, video });
					});

					// save a quick-reference property
					this.assign(order, {
						hiddenDetailsList,
						multiday,
						playable: orderPlayable,
						quantity,
						seasonType,
					});

					return order;
				},
			},

			getTransferDetails: {
				async: true,
				public: true,
				value(context, { orderId, orderOrigin }) {
					const { XHR } = this;
					let url;

					if (orderOrigin === `PURCHASE`) {
						url = `/api/orders/${orderId}/fan-transfer-offer`;
					} else if (orderOrigin === `TRANSFER`) {
						url = `/api/fan-transfers/${orderId}/fan-transfer-offer`;
					} else throw new Error(`Unrecognized orderOrigin: ${orderOrigin}`);

					return XHR.ajax({
						url,
						then: (response) => {
							const { saleOrders } = response;

							this.each(saleOrders, (saleOrder) => {
								const { deliveryType } = saleOrder;
								const friendlyLabel = DELIVERY_TYPE_LABELS[deliveryType];
								const deliveryTypeLabel = this.isString(friendlyLabel) ? friendlyLabel : deliveryType;

								this.assign(saleOrder, { deliveryTypeLabel });
							});

							return response;
						},
					});
				},
			},

			getTransferOffer: {
				async: true,
				public: true,
				value(context, { offerId }) {
					const { XHR } = this;
					const url = `/api/fan-transfer-offers/${offerId}`;

					return XHR.ajax({ url });
				},
			},

			acceptTransferOffer: {
				async: true,
				public: true,
				value(context, { offerId, body }) {
					const { XHR } = this;
					const url = `/api/fan-transfer-offers/${offerId}?action=ACCEPT`;
					const type = `PUT`;
					const REST = true;

					return XHR.ajax({ REST, type, url, body });
				},
			},

			cancelTransferOffer: {
				async: true,
				public: true,
				value(context, { offerId }) {
					const { XHR } = this;
					const url = `/api/fan-transfer-offers/${offerId}?action=CANCEL`;
					const type = `PUT`;

					return XHR.ajax({ type, url });
				},
			},

			declineTransferOffer: {
				async: true,
				public: true,
				value(context, { offerId, token }) {
					const { XHR } = this;
					// Workaround for email button click (no auth required)
					const type = token ? `POST` : `PUT`;
					const url = token
						? `/fan-transfer-offers/response?action=DECLINE&token=${token}`
						: `/api/fan-transfer-offers/${offerId}?action=DECLINE`;

					return XHR.ajax({ type, url });
				},
			},

			returnTransfer: {
				async: true,
				public: true,
				value(context, { id }) {
					const { User, XHR } = this;

					return XHR.ajax({
						type: `PUT`,
						url: `/api/fan-transfers/${id}?action=RETURN`,
						then: (response) => {
							// trigger a user refresh, transfer should be updated
							User.get({ force: true });

							return response;
						},
					});
				},
			},

			getUpgradableOrders: {
				async: true,
				public: true,
				value(context, { userId }) {
					const { XHR } = this;

					return XHR.ajax({
						type: `GET`,
						url: `/api/users/${userId}/upgrades`,
						ignoreErrors: true,
						then: (response) => {
							return response;
						},
					});
				},
			},

			getUpgradesByOrderId: {
				async: true,
				public: true,
				value(context, { orderId, userId }) {
					const { XHR } = this;

					return XHR.ajax({
						type: `GET`,
						url: `/api/users/${userId}/upgrades/${orderId}`,
						then: (response) => {
							return response.data;
						},
					});
				},
			},

			getInvoicePaymentCheckoutByOrderId: {
				async: true,
				public: true,
				value(context, { orderId }) {
					const { XHR } = this;

					return XHR.ajax({
						type: `GET`,
						url: `/api/paymentcheckout/orders/${orderId}`,
					});
				},
			},

			getMediaOrders: {
				async: true,
				public: true,
				async value(context, options) {
					try {
						await this.require({ deps: [`User`] });
						const { User, XHR } = this;

						if (User.isGuestUser()) {
							return [];
						}

						const user = await User.get();
						const { id: userId } = user;
						const url = `/api/users/${userId}/media-orders`;
						const response = await XHR.ajax({ type: `GET`, url });

						return this.map(response, (order) => {
							order.origin = `PURCHASE`;
							this.normalize(order);
							return order;
						});
					} catch (reason) {
						return [];
					}
				},
			},

			/**
			 * Gets the Apple Wallet link for the order
			 * @method getAppleWalletLink
			 * @returns {string} - The Apple Wallet link
			 * @async
			 * @public
			 * @param {object} options - The options for the Apple Wallet link
			 * @param {boolean} options.hasNFC - Whether the order has NFC
			 * @param {boolean} options.isMultiple - Defaults to false. Fetching the link for one ticket or multiple?
			 * @param {string} options.orderId - The order ID
			 * @param {string} options.orderOrigin - The order origin (PURCHASE or TRANSFER)
			 * @param {string} options.orderStatus - The order status
			 * @param {string} options.saleId - The sale ID
			 * @param {string} options.serialNumber - The serial number
			 */
			getAppleWalletLink: {
				async: true,
				public: true,
				async value(
					context,
					{ hasNFC, isMultiple = false, orderId, orderOrigin, orderStatus, saleId = null, serialNumber = null }
				) {
					const { Config, System, User, XHR } = this;
					const system = System.get();
					const { isiOS } = system;

					if (!isiOS || orderStatus === `QUEUED`) {
						return null;
					}

					let bindingUrl;
					let link;

					if (isMultiple) {
						if (orderOrigin === `PURCHASE`) {
							bindingUrl = `/api/orders/${orderId}/icloud-binding-request`;
							link = `/api/orders/${orderId}/apple-pass`;
						} else if (orderOrigin === `TRANSFER`) {
							bindingUrl = `/api/fan-transfers/${orderId}/icloud-binding-request`;
							link = `/api/fan-transfers/${orderId}/apple-pass`;
						}
					} else {
						if (orderOrigin === `PURCHASE`) {
							bindingUrl = `/api/orders/${orderId}/saleorders/${saleId}/patrons/${serialNumber}/icloud-binding-request`;
							link = `/api/orders/${orderId}/saleorders/${saleId}/patrons/${serialNumber}/apple-pass`;
						} else if (orderOrigin === `TRANSFER`) {
							bindingUrl = `/api/fan-transfers/${orderId}/patrons/${serialNumber}/icloud-binding-request`;
							link = `/api/fan-transfers/${orderId}/patrons/${serialNumber}/apple-pass`;
						}
					}

					if (hasNFC) {
						const user = await User.get({});
						const { id: userId } = user;
						const notificationsDomains = Config.NOTIFICATIONS_PROCESSOR_DOMAINS;
						const notificationsDomain = this.isString(notificationsDomains[Config.ENVIRONMENT], 1)
							? notificationsDomains[Config.ENVIRONMENT]
							: notificationsDomains[`prod`];
						const accountBindingRequest = await XHR.ajax({
							url: `${notificationsDomain}/api/accountbinding/users/${userId}`,
							type: `GET`,
						});

						if (!accountBindingRequest.hasAppleBinding) {
							// make a call to this new endpoint to kick off the binding flow

							const icloudBindingRequest = await XHR.ajax({
								url: bindingUrl,
								type: `GET`,
								ignoreErrors: true,
							});

							const { walletLink } = icloudBindingRequest;

							if (this.isString(walletLink, 1)) {
								// return the wallet link to make the api call to apple which will generate the bindingData for the user
								link = walletLink;
							}
						}
					}

					return link;
				},
			},
		},
	});
});

//# sourceMappingURL=Orders.js.map